import {
  AfterViewChecked,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  computed,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { ResponseListsService } from 'app/shared/services/response-lists.service';

export interface ResponseListItem {
  id: string;
  name: string;
  itemsAdded: number;
  lastItemAddedDate: Date | null;
}

@Component({
  selector: 'app-response-list-selector',
  templateUrl: './response-list-selector.component.html',
  styleUrls: ['./response-list-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ResponseListSelectorComponent implements OnInit, AfterViewChecked {
  private readonly responseListsService = inject(ResponseListsService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() placeholder = '';
  @Input() set disabled(value: boolean) {
    this.isDisabled = value;
  }
  @Input() set items(value: ResponseListItem[] | null) {
    if (value) {
      this._hasExternalItems = true;
      this.responseLists = value.map((item) => ({
        ...item,
        lastItemAddedDate: item.lastItemAddedDate ? new Date(item.lastItemAddedDate) : null,
      }));
      this.updateFilteredList();
      this.resolveSelectedItem();
    }
  }
  @Input() set value(val: string | null) {
    this._value = val;
    if (val && this.responseLists.length > 0) {
      this.selectedItem = this.responseLists.find((item) => item.id === val) ?? null;
    } else {
      this.selectedItem = val ? { id: val, name: '', itemsAdded: 0, lastItemAddedDate: null } : null;
    }
  }

  @ViewChild('dropdownSearchInput') private dropdownSearchInput: ElementRef<HTMLInputElement>;
  @Output() selectionChange = new EventEmitter<ResponseListItem>();

  protected isOpen = false;
  protected readonly searchControl = new FormControl('');
  protected filteredResponseLists: ResponseListItem[] = [];
  protected selectedItem: ResponseListItem | null = null;
  protected isDisabled = false;

  private readonly sortField = signal<'count' | 'date'>('count');
  private readonly sortAsc = signal(false);

  protected readonly sortIcon = computed(() => {
    return {
      count: this.sortField() === 'count' ? (this.sortAsc() ? 'arrow_upward' : 'arrow_downward') : 'unfold_more',
      date: this.sortField() === 'date' ? (this.sortAsc() ? 'arrow_upward' : 'arrow_downward') : 'unfold_more',
    };
  });

  protected readonly activeSortField = computed(() => this.sortField());
  protected readonly isSortAsc = computed(() => this.sortAsc());

  private responseLists: ResponseListItem[] = [];
  private _hasExternalItems = false;
  private _value: string | null = null;
  private shouldFocusSearch = false;

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateFilteredList());

    if (!this._hasExternalItems) {
      this.loadResponseLists();
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldFocusSearch && this.dropdownSearchInput) {
      this.dropdownSearchInput.nativeElement.focus();
      this.shouldFocusSearch = false;
    }
  }

  protected toggleDropdown(): void {
    if (this.isDisabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchControl.setValue('');
      this.updateFilteredList();
      this.shouldFocusSearch = true;
    }
  }

  protected closeDropdown(): void {
    this.isOpen = false;
  }

  protected selectItem(item: ResponseListItem): void {
    this.selectedItem = item;
    this.isOpen = false;
    this.selectionChange.emit(item);
  }

  protected toggleSort(field: 'count' | 'date'): void {
    if (this.sortField() === field) {
      this.sortAsc.update((v) => !v);
    } else {
      this.sortField.set(field);
      this.sortAsc.set(false);
    }
    this.updateFilteredList();
  }

  private updateFilteredList(): void {
    if (!this.responseLists) {
      this.filteredResponseLists = [];
      return;
    }
    const query = (this.searchControl.value || '').trim().toLowerCase();
    let filtered = this.responseLists;
    if (query.length > 0) {
      filtered = filtered.filter((item) => item.name?.toLowerCase().includes(query));
    }
    const direction = this.sortAsc() ? 1 : -1;
    this.filteredResponseLists = [...filtered].sort((a, b) => {
      if (this.sortField() === 'count') {
        return ((a.itemsAdded || 0) - (b.itemsAdded || 0)) * direction;
      } else {
        const dateA = a.lastItemAddedDate ? a.lastItemAddedDate.getTime() : 0;
        const dateB = b.lastItemAddedDate ? b.lastItemAddedDate.getTime() : 0;
        return (dateA - dateB) * direction;
      }
    });
  }

  private resolveSelectedItem(): void {
    if (this._value && !this.selectedItem?.name) {
      const found = this.responseLists.find((r) => r.id === this._value);
      if (found) this.selectedItem = found;
    }
  }

  private loadResponseLists(): void {
    this.responseListsService
      .getResponseListsWithStatistics()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        this.responseLists = (response ?? []).map((x) => ({
          id: x.id,
          name: x.name,
          itemsAdded: x.itemsAdded ?? 0,
          lastItemAddedDate: x.lastItemAddedDate ? new Date(x.lastItemAddedDate) : null,
        }));
        this.updateFilteredList();
        this.resolveSelectedItem();
      });
  }
}
